import { z } from "zod/v4";
import { DEFAULT_SCREENSHOT_INTERVAL_MINUTES, PROJECT_DEFAULT_PATH } from "./ea.consts";

import {
  AllowedWorkItemTypes,
  AssertActions,
  AssertionType,
  AssignedVariableType,
  CodeParamType,
  CodeType,
  CONDITIONAL_ACTION_TYPE,
  CONDITIONAL_TYPE,
  ComparisonLogicalOperator,
  ComparisonsOperators,
  ConditionTypes,
  DOCUMENTATION_TYPE,
  DataSourceComparisonType,
  DatasourceType,
  DocumentationStatus,
  EXECUTION_SCRIPT_TYPES,
  EXECUTION_STATE,
  EXECUTION_STATUS,
  ExportToCsvModels,
  FEATURES,
  GLOBAL_SETTINGS_KEYS,
  GlobalSettingsResolversModes,
  GlobalVariableType,
  INTERNAL_SETTINGS_KEYS,
  INTERRUPTION_TYPE,
  IssueTrackingToolConfigurationState,
  IssueTrackingToolTrigger,
  JOB_STATUS_TYPE,
  JOB_TRIGGER_TYPE,
  LicenseType,
  MESSAGE_TYPE,
  OverrideTypes,
  PLAY_MODE,
  PUBLIC_SETTINGS_KEYS,
  PathType,
  REAUTH_INTERVAL_TYPE,
  ROLES,
  RecurrenceType,
  RepeaterTypes,
  ReportExtension,
  ResolverType,
  SSO,
  STATUS_TYPE,
  SYSTEM_TYPE,
  SaveVideoCondition,
  SchedulerMode,
  ScriptAiRecoveryOptions,
  ScriptSpecialExecutionModes,
  ScriptStatus,
  StepAiRecoveryOptions,
  StepTerminationAction,
  StepTerminationType,
  SubscribeUpdateModelNames,
  SupportedLanguages,
  VIRTUAL_USER_AUTH_TYPE,
  VIRTUAL_USER_STATUS,
  VariableDefaultValueType,
  VariableType,
  VariableValueType,
  WebsocketMessageTypes,
  WorkItemSeverity,
  ErrorCodes,
} from "./ea.enums";
import {
  beginWithLettersThenNumbersAndLetters,
  beginWithLettersThenNumbersAndLettersWithoutUnderscore,
  noWhitespaceAtTheBeginning,
} from "./ea.regex";
import {
  CodeExecutionLogType,
  PlayerVisibility,
  RUNNER_OPEN_MODE,
  RUNNER_POSITION,
  RUNNER_TYPE,
} from "./ea.runner.types";
import { BooleanSchema, GENERATED_BY_DB_PROPERTIES, ZodUtils } from "./ea.schema.helpers";
import { numberSequenceFormatValidator } from "./ea.validators";
import { skipValidation } from "../utils/zod";
import { AiRecoveryData } from "./ea.types";
import { convertValue } from "./ea.utils";
// https://github.com/colinhacks/zod/pull/1675
// don't use zod.any, use z.record(z.unknown()) for now

const DateZodType = z.union([
  z
    .string()
    .datetime()
    .transform((d) => new Date(d)),
  z.coerce.date(),
]);

export const booleanSchema = z
  .union([z.boolean(), z.literal("true"), z.literal("false")])
  .transform((value) => value === true || value === "true");

const DecimalZodType = z.preprocess((v) => (v ? parseFloat(v.toString()) : v), z.number());

export const codeValueSchema = z.object({
  type: z.nativeEnum(CodeType).describe("Use NATIVE if you are asked to execute code"),
  value: z.string(),
  aiInstruction: z.string().optional(),
  aiSchema: z.string().optional(),
  assignedVariableId: z.number().optional(),
  assignedVariableType: z.nativeEnum(AssignedVariableType).optional(),
  params: z
    .array(
      z.object({
        value: z.string(),
        type: z.nativeEnum(CodeParamType),
      }),
    )
    .optional(),
});

const DatabaseId = z.number();

const clearableNumberSchema = z.preprocess(
  (val) => (val === "null" ? null : val),
  z.coerce.number().optional().nullable(),
);

const CommonProperties = {
  id: DatabaseId,
  createdAt: DateZodType,
  updatedAt: DateZodType,
  modifiedBy: z.string().default("Unknown"),
};

export const TokenSchema = z.object({
  id: z.string(),
  created: DateZodType,
  userId: z.number(),
  apiKey: z.boolean(),
  name: z.string().default(""),
  ttl: z.number().nonnegative(),
  sso: z.enum(SSO).optional().nullable(),
  scopes: z.string().optional().nullable(),
  // type: z.optional(z.enum(["Token", "ApiToken"])), //TODO transform apiKey property into type = ApiToken
});

const TokenUpdateSchema = TokenSchema.pick({ ttl: true, name: true, created: true }).extend({
  ttl: z.coerce.number(),
});

const TokenCreateSchema = TokenSchema.pick({ ttl: true, name: true }).extend({
  ttl: z.coerce.number(),
  name: z.string().min(3, "API Key name must contain at least 3 character(s)"),
});

export const TokenSchemas = {
  general: TokenSchema,
  create: ZodUtils.makeDefaultPropsOptional(TokenCreateSchema),
  update: TokenUpdateSchema,
};

const RoleSchema = z.object({
  id: DatabaseId,
  name: z.enum(ROLES),
  description: z.string().optional(),
});

export const RoleSchemas = ZodUtils.generateEntitySchemas(RoleSchema);

const RolesZodType = z.union([
  z.array(z.number()),
  z
    .array(RoleSchema)
    .default([])
    .transform((roles) => roles.map((r) => r.id)),
]);

const UserSchema = z.object({
  ...CommonProperties,

  realm: z.optional(z.string()).nullish(),
  password: z.string(),
  username: z.string(),
  email: z.string(),
  emailVerified: z.boolean().default(false),
  language: z.enum(SupportedLanguages).default(SupportedLanguages.EN),

  sso: z.enum(SSO).nullish(),
  isActive: z.boolean().default(false),

  settings: z.object({
    playMode: z.enum(PLAY_MODE).optional(),
    table: z
      .record(
        z.string(),
        z.object({
          hiddenColumns: z.array(z.string()).optional(),
          columns: z.array(z.string()).optional(),
        }),
      )
      .optional(),
    displayOnlyPublishedDocuments: z.boolean().optional(),
    reportExtension: z.enum(ReportExtension),
    hideAggregatedLogsView: z.boolean().optional(),
    documentationLocale: z.number(),
    its: z.number().optional().nullable(),
    runner: z
      .object({
        type: z.enum(RUNNER_TYPE).optional(),
        openMode: z.enum(RUNNER_OPEN_MODE).optional(),
        position: z.enum(RUNNER_POSITION).optional(),
      })
      .optional(),
    autoStart: z.boolean().optional(),
    slowMode: z.boolean().optional(),
    useSpecialMode: z.boolean().default(false),
    specialMode: z.enum(ScriptSpecialExecutionModes).optional().nullable(),
    playerVisibilityMode: z.enum(PlayerVisibility).optional().nullable(),
    slowExecution: z.coerce.number().optional().nullable(),
    saveVideoCondition: z.enum(SaveVideoCondition).optional().nullable(),
    dashboard: z
      .object({
        layout: z.any().optional().nullable(),
      })
      .optional()
      .nullable(),
  }),
  roles: z.array(RoleSchema).default([]),
});
const UserSchemaWithoutPass = UserSchema.omit({ password: true });

export const UserGroupSchema = z.object({
  ...CommonProperties,
  name: z.string(),
  users: z.array(UserSchemaWithoutPass).default([]).optional(),
  description: z.string().default(""),
});

const UserGroupEdit = {
  users: z.array(z.number()).optional(),
  name: z
    .string()
    .regex(noWhitespaceAtTheBeginning, { message: "Group name should not start with whitespace" }),
};

const UserGroupCreate = ZodUtils.makeOptionalPropsNullable(UserGroupSchema)
  .extend(UserGroupEdit)
  .omit(GENERATED_BY_DB_PROPERTIES)
  .partial({ users: true });

export const UserGroupSchemas = {
  general: UserGroupSchema,
  createData: ZodUtils.makeDefaultPropsOptional(UserGroupCreate),
  create: UserGroupCreate,
  update: ZodUtils.makeOptionalPropsNullable(
    UserGroupSchema.extend({
      name: z.string().regex(noWhitespaceAtTheBeginning, {
        message: "Group name should not start with whitespace",
      }),
    }),
  )
    .extend(UserGroupEdit)
    .omit(GENERATED_BY_DB_PROPERTIES)
    .partial(),
};

const UserEdit = {
  email: z.union([z.literal(""), z.string().email()]),
  groups: z
    .union([
      z.array(z.number()),
      z
        .array(UserGroupSchema)
        .default([])
        .transform((users) => users.map((r) => r.id)),
    ])
    .optional(),
  roles: RolesZodType.optional(),
};
const UserCreateSchema = ZodUtils.makeOptionalPropsNullable(
  UserSchema.omit({ email: true }).extend(UserEdit),
).omit(GENERATED_BY_DB_PROPERTIES);

export const ChangeUserPasswordSchema = z.object({
  oldPassword: z.string(),
  newPassword: z.string(),
  userId: z.number(),
});

export const UserSchemas = {
  // WORK: zod - leave deprecated merge here for now - https://github.com/colinhacks/zod/issues/4491
  general: UserSchemaWithoutPass.merge(z.object({ groups: z.array(UserGroupSchema).default([]) })),
  createData: ZodUtils.makeDefaultPropsOptional(UserCreateSchema),
  create: UserCreateSchema,
  update: ZodUtils.makeOptionalPropsNullable(UserCreateSchema).partial(),
  login: UserSchema.pick({ username: true, password: true }).extend({
    withLogout: booleanSchema.optional(),
  }),
  loginResponse: z.object({
    id: z.string(),
    userId: z.number(),
    user: UserSchema,
  }),
  changePassword: ChangeUserPasswordSchema,
};

export const DataSourceMetadataSchema = z.object({
  ...CommonProperties,
  name: z.string(),
  originalFilename: z.string(),
  sheetsMeta: z.record(z.string(), z.unknown()),
  jobId: z.number().nullish(),
  type: z.enum(DatasourceType),
  projectIds: z.array(z.number().optional()).optional(),
  migrated: z.boolean().optional().default(false),
});

export const GlobalSettingsAutoScreenshotsCleanupSchema = z.discriminatedUnion("enabled", [
  z.object({
    enabled: z.literal(true),
    days: z.number(),
    startTime: DateZodType,
    interval: z.number().default(DEFAULT_SCREENSHOT_INTERVAL_MINUTES),
  }),
  z.object({
    enabled: z.literal(false),
  }),
]);

export const HotJarSettingsSchema = z.object({
  enabled: z.boolean(),
  siteId: z.number(),
  version: z.number().optional(),
});

const ThrottlingSchema = z.object({
  beforeExecution: z.number().optional(), // todo: should not be optional but there are errors if we remove it, do we have corrupted data?
  afterExecution: z.number().optional(), // todo: should not be optional but there are errors if we remove it, do we have corrupted data?
});

export const MailerOptionsSchema = z.object({
  smtpServer: z.string().default(""),
  smtpPort: z.coerce.number().optional(),
  emailAddress: z.string().default(""),
  user: z.string().default(""),
  password: z.string().default(""),
  secure: booleanSchema.default(false),
});

export const SsoOptionsSchema = {
  [SSO.SAML]: z
    .object({ enabled: booleanSchema })
    .passthrough()
    .pipe(
      z.discriminatedUnion("enabled", [
        z.object({ enabled: z.literal(false) }),
        z.object({
          enabled: z.literal(true),
          config: z.object({
            issuer: z.string(),
            authorityHostUrl: z.string(),
            cert: z.string().optional(),
          }),
        }),
      ]),
    ),
  [SSO.AzureActiveDirectory]: z
    .object({ enabled: booleanSchema })
    .passthrough()
    .pipe(
      z.discriminatedUnion("enabled", [
        z.object({ enabled: z.literal(false) }),
        z.object({
          enabled: z.literal(true),
          config: z.object({
            authorityHostUrl: z.string(),
            tenant: z.string(),
            clientId: z.string(),
            clientSecret: z.string(),
          }),
        }),
      ]),
    ),
};

z.object({
  enabled: z.boolean().default(false),
});

export const GlobalSettingsInternalValuesSchema = z.object({
  [INTERNAL_SETTINGS_KEYS.DISABLED_FEATURES]: z.array(z.enum(FEATURES)),
  [INTERNAL_SETTINGS_KEYS.AUTO_SCREENSHOTS_CLEANUP]: GlobalSettingsAutoScreenshotsCleanupSchema,
  [INTERNAL_SETTINGS_KEYS.HOTJAR]: HotJarSettingsSchema,
});

const GlobalSettingsPublicValuesSchema = z.object({
  [PUBLIC_SETTINGS_KEYS.RECAPTCHA_PUBLIC_KEY]: z.string().optional(),
  [PUBLIC_SETTINGS_KEYS.RECAPTCHA_ENABLED]: z.boolean().optional(),
  [PUBLIC_SETTINGS_KEYS.SSO_AZURE_ENABLED]: z.boolean().optional(),
  [PUBLIC_SETTINGS_KEYS.SSO_SAML_ENABLED]: z.boolean().optional(),
});

export const GlobalSettingsValuesSchema = z.object({
  [GLOBAL_SETTINGS_KEYS.STATUS_TRANSITIONS]: z.record(
    z.enum(ScriptStatus),
    z.object({ initial: booleanSchema.optional(), to: z.array(z.enum(ScriptStatus)) }),
  ),
  [GLOBAL_SETTINGS_KEYS.PUBLISHING_CREDENTIALS_CONFIRMATION]: booleanSchema,
  [GLOBAL_SETTINGS_KEYS.DEFAULT_APP_USER_SETTINGS]: z.object({
    playMode: z.enum(PLAY_MODE).default(PLAY_MODE.FOREGROUND),
    reportExtension: z.enum(ReportExtension).default(ReportExtension.docx),
    runner: z.object({
      type: z.enum(RUNNER_TYPE).default(RUNNER_TYPE.ADVANCED),
      openMode: z.enum(RUNNER_OPEN_MODE).default(RUNNER_OPEN_MODE.NEW_WINDOW),
      position: z.enum(RUNNER_POSITION).default(RUNNER_POSITION.LEFT),
    }),
  }),
  [GLOBAL_SETTINGS_KEYS.TEST_PLANS_INTEGRATION]: booleanSchema.default(true),
  [GLOBAL_SETTINGS_KEYS.TIMEOUTS]: z.object({
    inactivityTimeout: z.coerce.number(),
    step: z.object({
      findElementTimeout: z.coerce.number(),
      readinessTimeout: z.coerce.number().default(1000 * 60 * 30),
    }),
    optionalStep: z.object({
      findElementTimeout: z.coerce.number(),
      readinessTimeout: z.coerce.number().default(1000 * 60 * 5),
    }),
    redirectTimeout: z.coerce.number(),
  }),
  [GLOBAL_SETTINGS_KEYS.MAILER_OPTIONS]: MailerOptionsSchema.optional().nullable(),
  [GLOBAL_SETTINGS_KEYS.SSO_SAML_OPTIONS]: SsoOptionsSchema[SSO.SAML],
  [GLOBAL_SETTINGS_KEYS.SSO_AZURE_OPTIONS]: SsoOptionsSchema[SSO.AzureActiveDirectory],
  [GLOBAL_SETTINGS_KEYS.REPORT_TEMPLATE]: clearableNumberSchema,
  [GLOBAL_SETTINGS_KEYS.DOCUMENTATION_TEMPLATE]: clearableNumberSchema,
  [GLOBAL_SETTINGS_KEYS.SUMMARY_TEMPLATE]: clearableNumberSchema,

  [GLOBAL_SETTINGS_KEYS.ADMINISTRATION_ADDRESS]: z.string().optional(),
  [GLOBAL_SETTINGS_KEYS.REPORTS_LINK]: z.string().optional().nullable(),
  [GLOBAL_SETTINGS_KEYS.RESOLVERS_MODE]: z.enum(GlobalSettingsResolversModes),
  [GLOBAL_SETTINGS_KEYS.POSITION_RESOLVER_META_SCORE_THRESHOLD]: clearableNumberSchema,
  [GLOBAL_SETTINGS_KEYS.GENERATE_RESOLVERS_ON_PLAYBACK]: booleanSchema.optional(),
  [GLOBAL_SETTINGS_KEYS.AI_OPTIONS]: z.object({
    chatEnabled: booleanSchema.default(false),
    voiceChatEnabled: booleanSchema.default(false),
    apiKey: z.string().optional(),
    model: z.string().optional(),
    speechKey: z.string().optional(),
    speechRegion: z.string().optional(),
    tokenLimits: z.coerce.number().min(3000).optional().default(1_000_000),
    aiRecovery: z.enum(["enabled", "enabledNoOptionalSteps", "disabled"]).default("disabled"),
    aiProvider: z.enum(["openai", "openrouter", "azure", "google", "anthropic"]).default("openai"),
    baseUrl: z.string().optional(),
  }),
  internal: GlobalSettingsInternalValuesSchema.optional(),
  public: GlobalSettingsPublicValuesSchema.optional(),
});

export const InternalSettingSchema = z.discriminatedUnion("id", [
  z.object({
    id: z.literal(INTERNAL_SETTINGS_KEYS.AUTO_SCREENSHOTS_CLEANUP),
    value:
      GlobalSettingsInternalValuesSchema.shape[INTERNAL_SETTINGS_KEYS.AUTO_SCREENSHOTS_CLEANUP],
  }),
  z.object({
    id: z.literal(INTERNAL_SETTINGS_KEYS.DISABLED_FEATURES),
    value: GlobalSettingsInternalValuesSchema.shape[INTERNAL_SETTINGS_KEYS.DISABLED_FEATURES],
  }),
  z.object({
    id: z.literal(INTERNAL_SETTINGS_KEYS.HOTJAR),
    value: GlobalSettingsInternalValuesSchema.shape[INTERNAL_SETTINGS_KEYS.HOTJAR],
  }),

  z.object({
    id: z.literal(PUBLIC_SETTINGS_KEYS.RECAPTCHA_PUBLIC_KEY),
    value: GlobalSettingsPublicValuesSchema.shape[PUBLIC_SETTINGS_KEYS.RECAPTCHA_PUBLIC_KEY],
  }),

  z.object({
    id: z.literal(PUBLIC_SETTINGS_KEYS.RECAPTCHA_ENABLED),
    value: GlobalSettingsPublicValuesSchema.shape[PUBLIC_SETTINGS_KEYS.RECAPTCHA_ENABLED],
  }),

  z.object({
    id: z.literal(PUBLIC_SETTINGS_KEYS.SSO_AZURE_ENABLED),
    value: GlobalSettingsPublicValuesSchema.shape[PUBLIC_SETTINGS_KEYS.SSO_AZURE_ENABLED],
  }),

  z.object({
    id: z.literal(PUBLIC_SETTINGS_KEYS.SSO_SAML_ENABLED),
    value: GlobalSettingsPublicValuesSchema.shape[PUBLIC_SETTINGS_KEYS.SSO_SAML_ENABLED],
  }),
]);

export const InternalSettingsSchema = z.array(InternalSettingSchema);

export const GlobalSettingSchema = z.discriminatedUnion("id", [
  z.object({
    id: z.literal(GLOBAL_SETTINGS_KEYS.STATUS_TRANSITIONS),
    value: GlobalSettingsValuesSchema.shape[GLOBAL_SETTINGS_KEYS.STATUS_TRANSITIONS],
  }),
  z.object({
    id: z.literal(GLOBAL_SETTINGS_KEYS.PUBLISHING_CREDENTIALS_CONFIRMATION),
    value:
      GlobalSettingsValuesSchema.shape[GLOBAL_SETTINGS_KEYS.PUBLISHING_CREDENTIALS_CONFIRMATION],
  }),
  z.object({
    id: z.literal(GLOBAL_SETTINGS_KEYS.DEFAULT_APP_USER_SETTINGS),
    value: GlobalSettingsValuesSchema.shape[GLOBAL_SETTINGS_KEYS.DEFAULT_APP_USER_SETTINGS],
  }),
  z.object({
    id: z.literal(GLOBAL_SETTINGS_KEYS.TEST_PLANS_INTEGRATION),
    value: GlobalSettingsValuesSchema.shape[GLOBAL_SETTINGS_KEYS.TEST_PLANS_INTEGRATION],
  }),
  z.object({
    id: z.literal(GLOBAL_SETTINGS_KEYS.TIMEOUTS),
    value: GlobalSettingsValuesSchema.shape[GLOBAL_SETTINGS_KEYS.TIMEOUTS],
  }),
  z.object({
    id: z.literal(GLOBAL_SETTINGS_KEYS.REPORT_TEMPLATE),
    value: GlobalSettingsValuesSchema.shape[GLOBAL_SETTINGS_KEYS.REPORT_TEMPLATE],
  }),
  z.object({
    id: z.literal(GLOBAL_SETTINGS_KEYS.DOCUMENTATION_TEMPLATE),
    value: GlobalSettingsValuesSchema.shape[GLOBAL_SETTINGS_KEYS.DOCUMENTATION_TEMPLATE],
  }),
  z.object({
    id: z.literal(GLOBAL_SETTINGS_KEYS.SUMMARY_TEMPLATE),
    value: GlobalSettingsValuesSchema.shape[GLOBAL_SETTINGS_KEYS.SUMMARY_TEMPLATE],
  }),
  z.object({
    id: z.literal(GLOBAL_SETTINGS_KEYS.ADMINISTRATION_ADDRESS),
    value: GlobalSettingsValuesSchema.shape[GLOBAL_SETTINGS_KEYS.ADMINISTRATION_ADDRESS],
  }),
  z.object({
    id: z.literal(GLOBAL_SETTINGS_KEYS.REPORTS_LINK),
    value: GlobalSettingsValuesSchema.shape[GLOBAL_SETTINGS_KEYS.REPORTS_LINK],
  }),
  z.object({
    id: z.literal(GLOBAL_SETTINGS_KEYS.RESOLVERS_MODE),
    value: GlobalSettingsValuesSchema.shape[GLOBAL_SETTINGS_KEYS.RESOLVERS_MODE],
  }),
  z.object({
    id: z.literal(GLOBAL_SETTINGS_KEYS.POSITION_RESOLVER_META_SCORE_THRESHOLD),
    value:
      GlobalSettingsValuesSchema.shape[GLOBAL_SETTINGS_KEYS.POSITION_RESOLVER_META_SCORE_THRESHOLD],
  }),
  z.object({
    id: z.literal(GLOBAL_SETTINGS_KEYS.GENERATE_RESOLVERS_ON_PLAYBACK),
    value: GlobalSettingsValuesSchema.shape[GLOBAL_SETTINGS_KEYS.GENERATE_RESOLVERS_ON_PLAYBACK],
  }),
  z.object({
    id: z.literal(GLOBAL_SETTINGS_KEYS.MAILER_OPTIONS),
    value: GlobalSettingsValuesSchema.shape[GLOBAL_SETTINGS_KEYS.MAILER_OPTIONS],
  }),
  z.object({
    id: z.literal(GLOBAL_SETTINGS_KEYS.AI_OPTIONS),
    value: GlobalSettingsValuesSchema.shape[GLOBAL_SETTINGS_KEYS.AI_OPTIONS],
  }),
  z.object({
    id: z.literal(GLOBAL_SETTINGS_KEYS.SSO_SAML_OPTIONS),
    value: GlobalSettingsValuesSchema.shape[GLOBAL_SETTINGS_KEYS.SSO_SAML_OPTIONS],
  }),
  z.object({
    id: z.literal(GLOBAL_SETTINGS_KEYS.SSO_AZURE_OPTIONS),
    value: GlobalSettingsValuesSchema.shape[GLOBAL_SETTINGS_KEYS.SSO_AZURE_OPTIONS],
  }),
]);

export const GlobalSettingsSchema = z.array(GlobalSettingSchema);
export const DatasourceSchema = z.object({
  ...CommonProperties,
  name: z.string(),
  originalFilename: z.string(),
  storageModifiedTime: z
    .union([
      z
        .string()
        .datetime()
        .transform((p) => new Date(p)),
      z.date(),
    ])
    .optional()
    .nullable(),
  sheetsMeta: z.record(z.string(), z.unknown()),
  jobId: z.number().optional().nullable(),
  temporary: z.boolean().optional().default(false),
  migrated: z.boolean().optional().default(false),
  type: z.string(),
});

export const GlobalSettingSchemas = {
  general: GlobalSettingSchema,
  update: GlobalSettingSchema,
};

export const DataSourceDataSchema = z.object({
  id: DatabaseId,
  sheet: z.string(),
  rowNumber: z.number(),
  dataSourceId: z.number(),
  rowData: z.record(z.string(), z.any()),
});

const KpiSchema = z.object({
  ...CommonProperties,
  name: z.string().regex(beginWithLettersThenNumbersAndLetters, {
    message: "Kpi name, must start with letter and contain only letter and numbers",
  }),
  description: z.string().default(""),
});

export const KpiLogDataSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export const KpiSchemas = ZodUtils.generateEntitySchemas(KpiSchema);

export const LabelParamsSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.undefined(), z.null(), z.boolean()]),
); // TODO: type for LabelParams;

export const MessageSchema = z.object({
  text: z
    .string()
    .optional()
    .nullable()
    .transform((v) => (v ? v : "")),
  type: z.enum(MESSAGE_TYPE),
  code: z.enum(ErrorCodes).optional(),
  metadata: z.record(z.string(), z.any()).optional(),
  labelKey: z.string().optional(),
  labelParams: LabelParamsSchema.optional(),
  showToast: z.boolean().optional(),
  isPlatformMessage: z.boolean().optional(),
  overrideStatus: z.enum(EXECUTION_STATUS).optional(),
  count: z.number().optional(),
});

const RuleMetadataSchema = z.object({
  id: z.string().optional(), // todo: optional?
  version: z.string().optional(),
  eventType: z.string().optional(), // todo: optional?
  platform: z.string().optional(),
  metadata: z.any().optional(),
});

export const WorkItemSchema = z.object({
  ...CommonProperties,
  title: z.string(),
  externalId: z.number(),
  type: z.enum(AllowedWorkItemTypes),
  url: z.string(),
  itsId: z.number().optional().nullable(),
  itsName: z.string(),
  scriptId: z.number().optional(),
  scriptName: z.string(),
  stepLabel: z.string(),
  logId: z.number().optional().nullable(),
  stepLogId: z.number().optional().nullable(),
  sessionId: z.string().optional(),
});

export const WorkItemSchemas = {
  general: WorkItemSchema,
  createData: ZodUtils.makeOptionalPropsNullable(WorkItemSchema).omit(GENERATED_BY_DB_PROPERTIES),
  create: ZodUtils.makeOptionalPropsNullable(WorkItemSchema).omit(GENERATED_BY_DB_PROPERTIES),
};

export const VariableLogsSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal(CodeExecutionLogType.CALCULATE),
    output: z.coerce.string(),
    input: z.string(),
    executedCode: z.string(),
  }),
  z.object({
    type: z.literal(CodeExecutionLogType.ASSIGN),
    output: z.record(z.string(), z.coerce.string()),
    input: z.string(),
    executedCode: z.string(),
    changedVariables: z.record(
      z.string(),
      z.object({
        name: z.string(),
        value: z.coerce.string(),
        type: z.enum(AssignedVariableType).optional(),
      }),
    ),
  }),
]);

export const MetadataStepLogSchema = z.object({
  ai: z
    .object({
      recovery: z
        .object({
          isRecovered: z.boolean(),
          summary: z.string(),
          type: z.enum(["prompt", "recoveryData"]),
          duration: z.coerce.number(),
          tokenUsage: z
            .object({
              promptTokens: z.number(),
              completionTokens: z.number(),
              cachedInputTokens: z.number(),
            })
            .optional(),
        })
        .optional(),
    })
    .optional(),
  context: z
    .object({
      envName: z.string().optional(),
      virtualUserName: z.string().optional(),
    })
    .optional(),
});

export const MetadataExecutionLogSchema = z.object({
  ai: z
    .object({
      recovery: z
        .object({
          successfulRecoveriesCount: z.number(),
          failedRecoveriesCount: z.number(),
          aiIntervention: z.boolean(),
        })
        .optional(),
    })
    .optional(),
});

export const ExecutionStepLogSchema = z.object({
  id: DatabaseId,
  executionId: z.number(),
  status: z.enum(EXECUTION_STATUS).default(EXECUTION_STATUS.NONE),
  label: z.string().default(""),
  lineNum: z.number().default(0),
  screenshotPath: z.string().default(""),
  variableLogs: VariableLogsSchema.optional(),
  messages: z.array(MessageSchema).optional(),
  linkedScriptId: z.number().optional(),
  parentStepLogId: z.number().default(0),
  stepData: z
    .object({
      id: z.number(),
      commandId: z.string(),
      platform: z.object({
        id: z.string(),
        version: z.string().optional(),
      }),
      rule: RuleMetadataSchema.optional().nullable(),
    })
    .optional(),
  workItem: WorkItemSchema.optional(),
  virtualUserId: z.number().optional(),
  labelKey: z.string().optional(),
  labelParams: z.any().optional(),
  kpi: z.array(KpiLogDataSchema).optional(),
  startTime: DateZodType.optional(),
  endTime: DateZodType.optional(),
  duration: z.coerce.bigint().optional(),
  metadata: MetadataStepLogSchema.optional(),
});

export const ExecutionStepLogSchemas = {
  general: ExecutionStepLogSchema,
  createData: ZodUtils.makeDefaultPropsOptional(
    ZodUtils.makeOptionalPropsNullable(ExecutionStepLogSchema).omit({ id: true }),
  ),
  create: ZodUtils.makeOptionalPropsNullable(ExecutionStepLogSchema).omit({ id: true }),
  update: ZodUtils.makeOptionalPropsNullable(ExecutionStepLogSchema).partial(),
};

export const ExecutionJobLogSchema = z.object({
  id: DatabaseId,
  jobId: z.string().optional(),
  jobExecutionId: z.string().optional(),
  startTime: DateZodType.optional(),
  endTime: DateZodType.optional(),
  status: z.enum(EXECUTION_STATUS),
  state: z.enum(EXECUTION_STATE),
  successCount: z.number().optional(),
  warningCount: z.number().optional(),
  errorCount: z.number().optional(),
  allCount: z.number().optional(),
  duration: z.coerce.bigint().optional(),
  projectId: z.number().optional(),
  projectName: z.string().optional(),
  schedulerJobName: z.string().optional(),
  schedulerJobId: z.number().optional(),
  isExcluded: z.boolean().default(false),
  integrationMetadata: z.record(z.string(), z.any()).optional(),
  runBlueprintId: z.number().optional(),
  reexecutionRootJobLogId: z.number().optional(),
  reexecutionParentJobLogId: z.number().optional(),
  reexecutionAttempt: z.number().optional(),
  reexecutionStatuses: z.array(z.enum(EXECUTION_STATUS)).optional(),
  isLatestReexecutionAttempt: z.boolean().optional(),
  subRows: z.array(z.any()).optional(),
});

export const CodeTemplateCodeParamsSchema = z.object({
  name: z.string(),
  description: z.string().nullable().optional(),
});
export const CodeTemplateDefinitionSchema = z.object({
  params: z.array(CodeTemplateCodeParamsSchema).optional(),
  code: z.string(),
});

export const CodeTemplateGroupSchema = z.object({
  ...CommonProperties,
  name: z.string().regex(beginWithLettersThenNumbersAndLetters, {
    message: "Code template group name, must start with letter and contain only letter and numbers",
  }),
  description: z.string().default(""),
  codeTemplates: z.array(z.any()).optional(),
});

export const CodeTemplateGroupSchemas = {
  general: CodeTemplateGroupSchema,
  createData: ZodUtils.makeDefaultPropsOptional(CodeTemplateGroupSchema),
  create: ZodUtils.makeOptionalPropsNullable(CodeTemplateGroupSchema).omit({
    ...GENERATED_BY_DB_PROPERTIES,
    codeTemplates: true,
  }),
  update: ZodUtils.makeOptionalPropsNullable(CodeTemplateGroupSchema).partial(),
};

export const CodeTemplateSchema = z.object({
  ...CommonProperties,
  name: z.string(),
  codeTemplateGroupId: z.number().default(0),
  activeVersion: z.number(),
  functionName: z.string().regex(beginWithLettersThenNumbersAndLetters, {
    message:
      "Code template function name, must start with letter and contain only letter and numbers",
  }),
  definitions: z.record(z.string().startsWith("v"), CodeTemplateDefinitionSchema),
  description: z.string().optional().default(""),
  codeTemplateGroup: CodeTemplateGroupSchema.partial().optional(), //TODO change how schema should work with transform when you fetch from database with select fields query eg.: {select: {name: true, id: true}}
});

const CodeTemplateCreateSchema = ZodUtils.makeOptionalPropsNullable(
  CodeTemplateSchema.extend({
    name: z.string().regex(noWhitespaceAtTheBeginning, {
      message: "Code Template name should not start with whitespace",
    }),
  }),
).omit(GENERATED_BY_DB_PROPERTIES);

export const CodeTemplatesSchemas = {
  general: CodeTemplateSchema,
  createData: ZodUtils.makeDefaultPropsOptional(CodeTemplateCreateSchema),
  create: CodeTemplateCreateSchema,
  update: CodeTemplateCreateSchema.partial(),
};

const GlobalVariableSchema = z.object({
  ...CommonProperties,
  name: z.string().regex(beginWithLettersThenNumbersAndLettersWithoutUnderscore, {
    message: "Global variable name must start with letter and contain only letter and numbers",
  }),
  type: z.enum(GlobalVariableType),
  valueType: z.enum(VariableDefaultValueType).optional(),
  value: z.string().default(""),
  isSensitive: z.boolean().default(false),
  description: z.string().default(""),
});

export const GlobalVariableSchemas = ZodUtils.generateEntitySchemas(GlobalVariableSchema);

export const NumberSequenceSchema = z.object({
  name: z.string().regex(beginWithLettersThenNumbersAndLetters, {
    message: "Number sequence name must start with letter and contain only letter and numbers",
  }),
  format: z.string().superRefine((format, ctx) => {
    const validateResult = numberSequenceFormatValidator(format);
    if (!validateResult) {
      return;
    }

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: validateResult,
    });
  }),
  nextval: z.union([
    z.string(),
    z.number().transform((a) => `${a}`),
    z.bigint().transform((a) => a.toString()),
  ]),
  ...CommonProperties,
});

export const NumberSequenceSchemas = {
  general: NumberSequenceSchema.omit({ format: true, name: true }).extend({
    format: z.string(),
    name: z.string(),
  }),
  create: ZodUtils.makeOptionalPropsNullable(NumberSequenceSchema)
    .omit(GENERATED_BY_DB_PROPERTIES)
    .extend({
      nextval: z.union([
        z.string(),
        z.number().transform((a) => `${a}`),
        z.bigint().transform((a) => a.toString()),
        z.undefined(),
      ]),
    }),
  update: ZodUtils.makeOptionalPropsNullable(NumberSequenceSchema).partial(),
};

export const VariableSchema = z.object({
  ...CommonProperties,
  parentId: z.number(),
  parentType: z.union([z.literal("TaskScript"), z.literal("Sequence"), z.literal("Global")]),
  name: z.string().regex(beginWithLettersThenNumbersAndLettersWithoutUnderscore, {
    message: "Variable name must start with letter and contain only letter and numbers",
  }),
  groupName: z.string().optional().nullable(),
  valueType: z.enum(VariableValueType).optional().nullable(),
  type: z.enum(VariableType).default(VariableType.Normal),
  defaultValue: z.string().optional().nullable(),
  description: z.string().default(""),
});

export const VariableMapSchema = z.object({
  stepId: z.number().optional(),
  linkId: z.number().optional().nullable(),
  sourceVariableId: z.number().optional().nullable(),
  destVariableId: z.number().optional().nullable(),
  variableType: z.enum(AssignedVariableType).optional().nullable(),
  sourceVariableType: z.enum(AssignedVariableType).optional().nullable(),
  destVariableType: z.enum(AssignedVariableType).optional().nullable(),
});

export const PatchVariablesSchema = z.object({
  parentId: z.number(),
  parentType: z.string(),
  variables: z
    .array(
      ZodUtils.makeDefaultPropsOptional(
        VariableSchema.omit(GENERATED_BY_DB_PROPERTIES).merge(
          z.object({ id: DatabaseId.optional() }),
        ),
      ),
    )
    .check((ctx) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const variables = ctx.value as any[];
      const names = variables.map((e) => e.name);
      if (variables.length !== new Set(names).size) {
        ctx.issues.push({
          code: "custom",
          message: `Variable names cannot be duplicated`,
          input: ctx.value,
        });
      }
    }),
});

export const VariableSchemas = {
  general: VariableSchema,
  createData: ZodUtils.makeDefaultPropsOptional(VariableSchema).omit(GENERATED_BY_DB_PROPERTIES),
  create: ZodUtils.makeOptionalPropsNullable(VariableSchema).omit(GENERATED_BY_DB_PROPERTIES),
  update: ZodUtils.makeOptionalPropsNullable(VariableSchema).partial(),
  patchDiff: PatchVariablesSchema,
};

export const LocalizationSchema = z.object({
  ...CommonProperties,
  locale: z.string(),
  description: z.string().default(""),
  needsAttention: z.boolean().default(false),
  translations: z.record(z.string(), z.unknown()),
});

export const LocalizationSchemas = {
  general: LocalizationSchema,
  create: ZodUtils.makeDefaultPropsOptional(LocalizationSchema)
    .omit({
      ...GENERATED_BY_DB_PROPERTIES,
      translations: true,
    })
    .extend({
      locale: z
        .string()
        .min(2)
        .refine((v) => v.trim() !== ""),
    }),
  update: ZodUtils.makeOptionalPropsNullable(LocalizationSchema)
    .extend({
      locale: z
        .string()
        .min(2)
        .refine((v) => v.trim() !== ""),
    })
    .partial(),
  import: LocalizationSchema.pick({ locale: true, description: true }).partial().extend({
    overwriteExisting: BooleanSchema.optional(),
  }),
};

export const ConditionValueSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.enum([
      ConditionTypes.STEP_VALUE,
      ConditionTypes.GLOBAL_CONSTANT,
      ConditionTypes.GLOBAL_MUTABLE,
      ConditionTypes.VARIABLE,
    ]),
    value: z.coerce.number(),
  }),
  z.object({
    type: z.enum([
      ConditionTypes.CONST,
      ConditionTypes.ADVANCED,
      ConditionTypes.EXPRESSION,
      ConditionTypes.NATIVE,
    ]),
    value: z.string(),
  }),
]);

const DataSourceComparisonSchema = z.object({
  type: z.literal(AssertionType.DATA_SOURCE),
  comparisonType: z.enum(DataSourceComparisonType),
  value: z.string(),
  resetOnLast: z.boolean().optional(),
  logicalOperator: z.enum(ComparisonLogicalOperator).optional(),
});

export const CheckValueComparisonSchema = z.object({
  type: z.literal(AssertionType.CHECK_VALUE),
  operator: z.enum(ComparisonsOperators),
  leftCondition: ConditionValueSchema,
  rightCondition: ConditionValueSchema,
  logicalOperator: z.enum(ComparisonLogicalOperator).optional(),
});

export const ActivityActionSchema = z.object({
  type: z.enum(AssertActions),
  values: z.array(z.any()),
  message: z.string().optional(),
  overrideStatus: z.boolean().optional(),
  overrideStatusTo: z.enum(EXECUTION_STATUS).optional(),
});

export const ActivityExecution = z.object({
  ifAction: ActivityActionSchema,
  elseAction: ActivityActionSchema,
});

export const ActivitySchema = ActivityExecution.merge(
  z.object({
    comparison: z.array(z.union([DataSourceComparisonSchema, CheckValueComparisonSchema])),
    ifAction: ActivityActionSchema,
    elseAction: ActivityActionSchema,
  }),
);

export const ReactionSchema = z.discriminatedUnion("activityType", [
  z.object({
    activityType: z.literal(CONDITIONAL_ACTION_TYPE.SIMPLE),
    reactOn: z.union([z.enum(INTERRUPTION_TYPE), z.literal(CONDITIONAL_TYPE.SUCCESS)]),
    activity: ActivityActionSchema,
  }),
  z.object({
    activityType: z.literal(CONDITIONAL_ACTION_TYPE.ADVANCED),
    reactOn: z.union([z.enum(INTERRUPTION_TYPE), z.literal(CONDITIONAL_TYPE.SUCCESS)]),
    activity: ActivitySchema,
  }),
]);

const GuardSchemaChunk = z.object({
  ...CommonProperties,
  name: z.string().default(""),
  scriptId: z.number(),
  type: z.enum(CONDITIONAL_TYPE),
  reactions: z.array(ReactionSchema),
  kpis: z.array(KpiSchema).optional(),
});

const StepSchema = z.object({
  ...CommonProperties,

  paths: z
    .array(
      z.object({
        path: z.string(),
        type: z.enum(PathType),
      }),
    )
    .default([]),
  resolvers: z
    .object({
      metadata: z.array(z.record(z.string(), z.any())),
      data: z.array(z.any()),
    })
    .optional(),
  isDisabled: z.boolean().default(false),
  isOptional: z.boolean().default(false),
  isOneTime: z.boolean().default(false),
  isTimeoutEnabled: z.boolean().default(false),
  takeScreenshots: z.boolean().optional().nullable(),
  aiRecovery: z.enum(StepAiRecoveryOptions).default(StepAiRecoveryOptions.auto),
  aiRecoveryData: skipValidation<AiRecoveryData>().optional().nullable(),
  manualLabel: z.boolean().default(false),
  comment: z.string().default(""),
  commandId: z.string(),
  rule: RuleMetadataSchema.optional(),
  label: z.string(),
  labelKey: z.string().optional(),
  hasBeenLabelModified: z.boolean().optional(),
  lineNum: z.number(),
  value: z.any().optional(),
  platform: z
    .object({
      id: z.string().default("DEFAULT_PLATFORM"),
      version: z.string().default(""),
      options: z
        .object({
          forceOpenPopup: z.boolean().nullable().optional(),
        })
        .optional(),
    })
    .default({
      id: "DEFAULT_PLATFORM", // todo: can't get DEFAULT_PLATFORM_ID here, ADD TO DB
      version: "",
    }),
  termination: z
    .object({
      type: z.enum(StepTerminationType),
      value: z.coerce.number(),
      action: z.enum(StepTerminationAction),
    })
    .optional(),
  timeout: z.coerce.number().optional(),
  preExecutionData: z
    .object({
      triggersRefresh: z.boolean().optional(),
      value: z.any().optional(),
    })
    .optional(),
  taskScriptId: z.number(),
  linkedScriptId: z.number().optional(),
  linkName: z.string().optional(),

  executionCount: z.number().optional(),
  averageDuration: DecimalZodType.optional(),
  structureVersion: z.string().default("0.0.1"),

  triggersRefresh: z.boolean().default(false),

  disableLinkedStartStep: z.boolean().default(false),
  delay: z.coerce.number().optional(),
  frames: z
    .array(
      z.object({
        className: z.string().optional(),
        id: z.string().optional(),
        name: z.string().optional(),
        src: z.string().optional(),
        title: z.string().optional(),
        parentId: z.string().optional(),
        parentTagName: z.string().optional(),
        parentAttributes: z
          .array(
            z.object({
              name: z.string(),
              value: z.string(),
            }),
          )
          .optional(),
        iframeIndex: z.number().optional(),
        ariaLabel: z.string().optional().optional().nullable(),
      }),
    )
    .optional(),

  throttling: ThrottlingSchema.optional(),

  labelParams: LabelParamsSchema.optional(),
  guards: z.lazy(() => z.array(GuardSchemaChunk).default([])),
  codeTemplates: z.lazy(() => z.array(CodeTemplateSchema).optional()),
  files: z.array(z.string()).default([]).optional(),
  version: z.string().optional(),
});

const StepCreateSchema = ZodUtils.makeOptionalPropsNullable(
  StepSchema.extend({
    label: z.string().regex(noWhitespaceAtTheBeginning, {
      message: "Step name can't start with whitespace characters",
    }),
  }),
)
  .omit(GENERATED_BY_DB_PROPERTIES)
  .partial({
    lineNum: true,
    label: true,
    guards: true,
  });

export const StepSchemas = {
  general: StepSchema.extend({
    generatedLabel: z.string().optional(),
  }),
  created: ZodUtils.makeOptionalPropsNullable(StepSchema).omit(GENERATED_BY_DB_PROPERTIES),
  createData: ZodUtils.makeDefaultPropsOptional(StepCreateSchema),
  create: StepCreateSchema,
  update: ZodUtils.makeOptionalPropsNullable(
    StepSchema.extend({
      generatedLabel: z.string().optional(),
      label: z.union([
        z.literal(""),
        z.string().regex(noWhitespaceAtTheBeginning, {
          message: "Step name can't start with whitespace characters",
        }),
      ]),
    }),
  ).partial(),
};

const GuardSchema = GuardSchemaChunk.extend({ steps: z.array(StepSchema).optional() });
const GuardCreateSchema = ZodUtils.makeOptionalPropsNullable(
  GuardSchemaChunk.extend({ steps: z.array(z.number()).min(1) }).omit(GENERATED_BY_DB_PROPERTIES),
);

export const GuardSchemas = {
  general: GuardSchema,
  createData: ZodUtils.makeDefaultPropsOptional(GuardCreateSchema),
  create: GuardCreateSchema,
  update: GuardCreateSchema.partial(),
};

export const LogMessageSchema = z.object({
  labels: z.array(
    StepSchema.pick({ label: true, lineNum: true, labelKey: true, labelParams: true }).merge(
      z.object({
        label: z.string().nullable().optional(),
        labelKey: z.string().nullable().optional(),
        labelParams: LabelParamsSchema.optional().nullable(),
      }),
    ),
  ),
  level: z.number(),
  path: z.string(),
  messages: z.array(
    MessageSchema.pick({
      type: true,
      text: true,
      code: true,
      metadata: true,
      labelKey: true,
      labelParams: true,
    }),
  ),
});

export const ExecutionLogSchema = z.object({
  id: DatabaseId,
  sessionId: z.string(),
  startTime: DateZodType.optional(),
  endTime: DateZodType.optional(),
  isBackground: z.boolean().default(false),
  type: z.enum(EXECUTION_SCRIPT_TYPES).optional(),
  internal: z.boolean().default(false),
  state: z.enum(EXECUTION_STATE),
  scriptStatus: z.union([z.enum(ScriptStatus), z.literal("")]).optional(),
  status: z.enum(EXECUTION_STATUS).default(EXECUTION_STATUS.NONE),
  url: z.string().default(""),
  duration: z.coerce.bigint().optional(),
  callerId: z.number().optional(),
  iteration: z.number().default(0),
  scriptId: z.number().optional(),
  scriptName: z.string().default(""),
  scriptFlowId: z.string().optional(),
  blueprintNodeId: z.string().optional(),
  carriedOverFromExecutionLogId: z.number().optional(),
  schedulerJobId: z.number().optional(),
  schedulerJobName: z.string().default(""),
  apiKeyName: z.string().default(""),
  projectId: z.number().optional(),
  projectName: z.string().default(""),
  messages: z.array(LogMessageSchema).default([]),
  scriptVersion: z.string().default(""),
  jobId: z.string().optional(),
  jobExecutionId: z.string().optional(),
  tags: z.array(z.string()).default([]),
  integrationMetadata: z.record(z.string(), z.any()).optional(),
  mappedWithDevops: z.boolean().default(false),
  envName: z.string().default(""),
  virtualUserName: z.string().default(""),
  executorName: z.string().default(""),
  isSessionActive: z.boolean().default(false),
  scriptDescription: z.string().default(""),
  scriptSignedBy: z.string().optional(),
  lastErrorMessage: z.string().optional(),
  metadata: MetadataExecutionLogSchema.optional(),
});

export const ExecutionLogSchemas = {
  general: ExecutionLogSchema,
  createData: ZodUtils.makeDefaultPropsOptional(
    ZodUtils.makeOptionalPropsNullable(ExecutionLogSchema).omit({
      id: true,
      lastErrorMessage: true,
    }),
  ),
  create: ZodUtils.makeOptionalPropsNullable(ExecutionLogSchema).omit({
    id: true,
    lastErrorMessage: true,
  }),
  update: ZodUtils.makeOptionalPropsNullable(ExecutionLogSchema)
    .omit({ lastErrorMessage: true })
    .partial(),
};

export const ExecutionStepLogsOverviewSchema = z.object({
  title: z.string(),
  messages: z.array(MessageSchema),
  screenshot: z.string(),
  screenshotPath: z.string(),
  stepLogId: z.number(),
  workItem: WorkItemSchema.optional(),
  lineNum: z.number(),
});

export const ExecutionLogOverviewSchema = z.object({
  id: z.number(),
  scriptName: z.string(),
  stepLogs: ExecutionStepLogsOverviewSchema.array(),
});

export const ExecutionLogsStatsSchema = z.object({
  statuses: z.object({
    total: z.number(),
    [STATUS_TYPE.NONE]: z.number(),
    [STATUS_TYPE.ERROR]: z.number(),
    [STATUS_TYPE.WARNING]: z.number(),
    [STATUS_TYPE.SUCCESS]: z.number(),
  }),
  messages: z.array(
    z.object({
      messageText: z.string(),
      messageType: z.enum(MESSAGE_TYPE),
      stepLabel: z.string(),
      lineNum: z.number(),
      scriptId: z.number().nullable(),
      scriptName: z.string(),
    }),
  ),
});

export const SystemUrlSchema = z.object({
  id: DatabaseId,
  name: z.string(),
  url: z.string(),
});

const SystemSchema = z.object({
  ...CommonProperties,
  envName: z.string(),
  envAddress: z.string(), // todo: NEW_TYPES .url(), doesn't pass
  envType: z.enum(SYSTEM_TYPE).default(SYSTEM_TYPE.OTHER),
  applicationName: z.string().default(""),
  description: z.string().default(""),
  userId: z.number().optional(),
  systemPlatform: z.string().default("DEFAULT_PLATFORM"),
  options: z
    .object({
      enableMultiWindowsSupport: z.boolean().optional(),
      disableWebSecurity: z.boolean().optional(),
    })
    .strict()
    .optional(),
  meta: z.record(z.string(), z.any()).optional(),
});

const SystemCreateSchema = ZodUtils.makeOptionalPropsNullable(SystemSchema).omit(
  GENERATED_BY_DB_PROPERTIES,
);

export const SystemSchemas = {
  general: SystemSchema,
  createData: ZodUtils.makeDefaultPropsOptional(SystemCreateSchema),
  create: SystemCreateSchema,
  update: ZodUtils.makeOptionalPropsNullable(SystemSchema).partial(),
};

const CommonSystemDataSchema = z.object({
  isManual: z.boolean().optional(),
  rememberSensitiveData: z.boolean().optional(),
});

export const SystemDataSchema = z
  .discriminatedUnion("useCustomScript", [
    z.object({
      useCustomScript: z.literal(true),
      customScriptId: z.coerce.number(),
      values: z.array(
        z.object({
          key: z.string(),
          value: z.string(),
          isSensitive: z.boolean().optional(),
          isSystemLogin: z.boolean().optional(),
        }),
      ),
    }),
    z.object({
      useCustomScript: z.literal(false),
      platform: z.string(),
      values: z.record(
        z.string(),
        z.object({ value: z.any(), isSensitive: z.boolean().optional() }),
      ),
    }),
    z.object({
      useCustomScript: z.literal(undefined),
      platform: z.string(),
      values: z.record(
        z.string(),
        z.object({ value: z.any(), isSensitive: z.boolean().optional() }),
      ),
    }),
  ])
  .and(CommonSystemDataSchema);

const SystemDataSchemaChunk = z.discriminatedUnion("isSystemAuthEnabled", [
  z.object({ isSystemAuthEnabled: z.literal(true), systemData: SystemDataSchema }),
  z.object({
    isSystemAuthEnabled: z.literal(false),
    systemData: z.looseObject({}).optional().nullable(),
  }),
  z.object({
    isSystemAuthEnabled: z.undefined(),
    systemData: z.undefined(),
  }),
]);

export const ServerDataSchemaChunk = z.discriminatedUnion("serverAuthType", [
  z.object({
    serverAuthType: z.literal(VIRTUAL_USER_AUTH_TYPE.BASIC_AUTH),
    serverData: z.object({
      login: z.string(),
      password: z.string(),
    }),
  }),
  z.object({
    serverAuthType: z.literal(VIRTUAL_USER_AUTH_TYPE.NTLM),
    serverData: z.object({
      login: z.string(),
      password: z.string(),
      workstation: z.string().optional(),
      domain: z.string().optional(),
    }),
  }),
  z.object({
    serverAuthType: z.literal(VIRTUAL_USER_AUTH_TYPE.NONE),
    serverData: z
      .object({
        login: z.string().optional().nullable(),
        password: z.string().optional().nullable(),
      })
      .partial()
      .optional()
      .nullable(),
  }),
  z.object({
    serverAuthType: z.undefined(),
    serverData: z
      .object({
        login: z.string().optional().nullable(),
        password: z.string().optional().nullable(),
      })
      .partial()
      .optional()
      .nullable(),
  }),
]);

const VirtualUserCommon = {
  name: z.string(),
  description: z.string().optional(),
  status: z.enum(VIRTUAL_USER_STATUS).default(VIRTUAL_USER_STATUS.NOT_AUTHENTICATED),
  serverAuthType: z.enum(VIRTUAL_USER_AUTH_TYPE).default(VIRTUAL_USER_AUTH_TYPE.NONE),
};

const autoAuthDataSchema = z.discriminatedUnion("reauthenticate", [
  z.object({
    reauthenticate: z.literal(true),
    lastAutoAuthDate: DateZodType.optional(),
    startAutoAuthDate: DateZodType,
    interval: z.object({
      type: z.enum(REAUTH_INTERVAL_TYPE),
      multiplier: z.coerce.number().min(1),
    }),
    retryCount: z.coerce.number().optional().nullable(),
  }),
  z.object({
    reauthenticate: z.literal(false),
  }),
  z.object({
    reauthenticate: z.literal(undefined),
  }),
]);

const storageData = z
  .tuple([z.array(z.string()), z.array(z.string())])
  .catch([[], []])
  .default([[], []]);

export const VirtualUserTokenSchema = z.object({
  cookies: z.any().optional(),
  localStorage: storageData,
  sessionStorage: storageData,
});

const VirtualUserDb = {
  ...CommonProperties,
  lastAuthenticationDate: DateZodType.optional(),
  errorMessage: z.string().optional(),
  errorScreenshotPath: z.string().optional(),
  screenshotsOnlyOnInterruption: z.boolean().default(false),
  tokens: VirtualUserTokenSchema.optional(),
  nextAutoAuthenticationDate: DateZodType.optional().nullable(),
  autoAuthData: autoAuthDataSchema.optional(),
};

export const VirtualUserSchemaBase = z.object({
  ...VirtualUserDb,
  ...VirtualUserCommon,
});

const VirtualUserSchemaBaseNullable = ZodUtils.makeOptionalPropsNullable(
  VirtualUserSchemaBase.extend({
    name: z.string().regex(noWhitespaceAtTheBeginning, {
      message: "Virtual User name should not start with whitespace",
    }),
  }),
).omit(GENERATED_BY_DB_PROPERTIES);

export const VirtualUserSchemas = {
  // virtual user uses discriminated union types and we need to call null => undefined here
  generalMapper: ZodUtils.convertNullsToUndefinedInOptionalProps(VirtualUserSchemaBase)
    .partial()
    .and(ServerDataSchemaChunk)
    .and(SystemDataSchemaChunk),
  general: VirtualUserSchemaBase.and(ServerDataSchemaChunk).and(SystemDataSchemaChunk),
  createData: ZodUtils.makeDefaultPropsOptional(VirtualUserSchemaBaseNullable)
    .and(ServerDataSchemaChunk)
    .and(SystemDataSchemaChunk),
  create: VirtualUserSchemaBaseNullable.and(ServerDataSchemaChunk).and(SystemDataSchemaChunk),
  update: VirtualUserSchemaBaseNullable.partial()
    .and(ServerDataSchemaChunk)
    .and(SystemDataSchemaChunk),
};

export const LicenseSchema = z.object({
  type: z.enum(LicenseType),
  expirationDate: DateZodType.optional(),
  supportExpirationDate: DateZodType.optional(),
  maximumParallelSessions: z.coerce.number().min(0).default(0),
  maximumConcurrentUsers: z.coerce.number().min(0).default(0),
  licenseNumber: z.string().optional(),
  maximumScripts: z.coerce.number().min(0).default(0),
  scriptReplaysPerYearLimit: z.coerce.number().min(0).default(0),
  maxUsers: z.coerce.number().min(0).default(0),
  maximumExcelFileSize: z.coerce.number().min(0).default(0),
});

// todo: finish schema
export const BackgroundStartParams = z.looseObject({});

export const ProjectCategorySchema = z.object({
  ...CommonProperties,
  name: z.string(),
  description: z.string().default(""),
});

export const ProjectCategorySchemas = ZodUtils.generateEntitySchemas(ProjectCategorySchema);

export const TagSchema = z.object({
  ...CommonProperties,
  name: z.string(),
  description: z.string().default(""),
});

export const TagSchemas = ZodUtils.generateEntitySchemas(TagSchema);

export const TaskScriptTagSchema = z.object({
  id: DatabaseId,
  taskScriptId: z.number().optional(),
  tagId: z.number().optional(),
});

export const AdditionalJobParamSchema = z.object({
  noPageCache: z.boolean().default(false),
  recordVideo: z.boolean().default(false),
});

export const BackgroundStartParamsScheme = z.object({
  specialMode: z.enum(ScriptSpecialExecutionModes).nullable(),
  useSpecialMode: z.boolean().default(false).nullable(),
  playerVisibilityMode: z.enum(PlayerVisibility).nullable(),
  saveVideoCondition: z.enum(SaveVideoCondition).nullable(),
  slowExecution: z.coerce.number().nullable(),
  jobId: z.string(),
  documentationLocale: z.number().nullable(),
  schedulerJobId: z.number(),
  notify: z.boolean().default(false),
  datasourceId: z.number().nullable(),
  additionalStartParams: AdditionalJobParamSchema,
  dispatchStartTime: DateZodType.nullable(),
  azureConfigurationId: z.number().nullable(),
});

export const IssueTrackingToolCustomFieldSchema = z.object({
  name: z.string(),
  value: z.any(),
});

export const AzureDevopsIntegrationMetadataSchema = z.object({
  iteration: z.string(),
  project: z.string(),
  priority: z.number(),
  area: z.string().optional(),
});

const IssueTrackingIntegrationMetadata = z.object({
  azureDevops: AzureDevopsIntegrationMetadataSchema,
});

const IssueTrackingToolConnectionParams = z.object({
  host: z.string(),
  token: z.string(),
  project: z.string(),
});

export const BaseAzureIntegrationMetadataSchema = z.object({
  testPlanId: z.string(),
  testSuiteId: z.string(),
  itsId: z.number(),
  project: z.string(),
});

export const ScriptIntegrationMetadataSchema = z.object({
  azureDevops: z
    .object({
      testCaseId: z.string(),
      testCaseUrl: z.string(),
    })
    .merge(BaseAzureIntegrationMetadataSchema),
});

export const IssueTrackingToolSchema = z
  .object({
    ...CommonProperties,
    // project: z.string(), // probably should not be used anymore > moved to integrationmetadata
    name: z.string(),
    severity: z.enum(WorkItemSeverity),
    priority: z.preprocess((v) => `${v}`, z.string()),
    iteration: z.string(),
    customFields: z.array(IssueTrackingToolCustomFieldSchema),
    type: z.enum(AllowedWorkItemTypes),
    integrationMetadata: IssueTrackingIntegrationMetadata,
    area: z.string().optional(),
  })
  .merge(IssueTrackingToolConnectionParams);

const IssueTrackingToolCreateExtender = {
  name: z.string().regex(noWhitespaceAtTheBeginning, {
    message: "Integration Tracker name should not start with whitespace",
  }),
};

export const IssueTrackingToolSchemas = {
  general: IssueTrackingToolSchema,
  createData: ZodUtils.makeDefaultPropsOptional(IssueTrackingToolSchema)
    .extend(IssueTrackingToolCreateExtender)
    .omit(GENERATED_BY_DB_PROPERTIES),
  create: ZodUtils.makeOptionalPropsNullable(IssueTrackingToolSchema)
    .extend(IssueTrackingToolCreateExtender)
    .omit(GENERATED_BY_DB_PROPERTIES),
  update: IssueTrackingToolSchema.extend(IssueTrackingToolCreateExtender).partial(),
};

export const DevopsProjectsExportParamsSchema = z.object({
  itsId: z.coerce.number(),
  projectIds: z.array(z.coerce.number()),
  iteration: z.string().optional(),
  project: z.string().optional(),
  priority: z.number().optional(),
  area: z.string().optional(),
  fixedTestplanMapping: z.looseObject({}).optional(),
});

export const DevopsScriptExportParamsSchema = z.object({
  itsId: z.number(),
  scriptId: z.number(),
  iteration: z.string().optional(),
  project: z.string().optional(),
  priority: z.number().optional(),
  area: z.string().optional(),
});

export const IssueTrackingRuleSchema = z.object({
  name: z.string(),
  itsId: z.number(),
  triggers: z.array(z.enum(IssueTrackingToolTrigger)).min(1),
  config: z
    .object({
      severity: z.enum(WorkItemSeverity),
      priority: z.coerce.number(),
      iteration: z.string(),
      customFields: z.array(IssueTrackingToolCustomFieldSchema),
      type: z.enum(AllowedWorkItemTypes),
      project: z.string(),
      area: z.string(),
    })
    .partial(),
  itsName: z.string().default(""),
  ...CommonProperties,
});

const IssueTrackingRuleCreateExtender = {
  name: z
    .string()
    .regex(noWhitespaceAtTheBeginning, { message: "Rule name should not start with whitespace" }),
};

export const IssueTrackingRuleSchemas = {
  general: IssueTrackingRuleSchema,
  createData: ZodUtils.makeDefaultPropsOptional(IssueTrackingRuleSchema)
    .extend(IssueTrackingRuleCreateExtender)
    .omit(GENERATED_BY_DB_PROPERTIES),
  create: ZodUtils.makeOptionalPropsNullable(IssueTrackingRuleSchema)
    .extend(IssueTrackingRuleCreateExtender)
    .omit(GENERATED_BY_DB_PROPERTIES),
  // .partial({ itsName: true }),
  update: ZodUtils.makeOptionalPropsNullable(IssueTrackingRuleSchema)
    .extend(IssueTrackingRuleCreateExtender)
    .partial(),
};

export const IssueTrackingConfigurationSchema = z.object({
  ...CommonProperties,
  name: z.string(),
  description: z.string().optional(),
  itsId: z.number(),
  state: z.enum(IssueTrackingToolConfigurationState),
  integrationMetadata: z.object({
    azureDevops: z.object({ id: z.number().optional(), project: z.string().optional() }),
  }),
});

export const IssueTrackingConfigurationSchemas = {
  general: IssueTrackingConfigurationSchema,
  createData: ZodUtils.makeDefaultPropsOptional(IssueTrackingConfigurationSchema).omit(
    GENERATED_BY_DB_PROPERTIES,
  ),
  create: IssueTrackingConfigurationSchema.omit(GENERATED_BY_DB_PROPERTIES),
  update: IssueTrackingConfigurationSchema.omit(GENERATED_BY_DB_PROPERTIES),
};

export const TaskScriptSchema = z.object({
  ...CommonProperties,
  name: z.string(),
  startUrl: z.string().optional(),
  version: z.string().optional(),
  creatorId: z.number().optional(),
  description: z.string().default(""),
  hideComments: z.boolean().default(false),
  showActionHint: z.boolean().default(false),
  isManualUrl: z.boolean().default(false),
  status: z.enum(ScriptStatus).default("DRAFT"),
  environmentId: z.number().optional(),
  takeScreenshots: z.boolean().default(false),
  documents: z.number().default(0),
  publishedDocuments: z.number().default(0),
  skipLinkedScriptsInDocumentation: z
    .boolean()
    .nullable()
    .transform((v) => v!)
    .default(false),
  disableStartStep: z
    .boolean()
    .nullable()
    .transform((v) => v!)
    .default(false),
  projectId: z.number().optional(),
  timeout: z.coerce.number().optional(),
  clickOnDisabledButtons: z.boolean().default(false),
  integrationMetadata: z.any(), // ScriptIntegrationMetadataSchema.optional(), // TODO: type for integration metadata
  reportTemplate: z.number().optional(),
  documentationTemplate: z.number().optional(),
  virtualUserId: z.number().optional(),
  groupid: z.number().optional(),
  autoRefreshDatasource: z.boolean().default(false),
  aiRecovery: z.enum(ScriptAiRecoveryOptions).default(ScriptAiRecoveryOptions.auto),
  throttling: ThrottlingSchema.optional(), // TODO: type for throttling data
  useVirtualUser: z.boolean().default(false),
  screenshotsOnlyOnInterruption: z.boolean().default(false),
  resolvers: z
    .object({
      enabled: z.boolean().optional(),
      mode: z.enum(GlobalSettingsResolversModes).optional(),
    })
    .optional(),
  environment: SystemSchemas["general"]
    .pick({ envName: true, envAddress: true, envType: true })
    .optional(),
  tags: z.array(TagSchema).optional(),
  globalVariables: z.lazy(() => z.array(GlobalVariableSchema).optional()).optional(),
  stepCount: z.number().optional(),
  lastExecutionStatus: z.enum(EXECUTION_STATUS).optional().nullable(),
  lastExecutorName: z.string().optional().nullable(),
  lastExecutionDate: DateZodType.optional().nullable(),
  appUser: z
    .lazy(() => UserSchemas["general"].pick({ id: true, username: true }).partial().optional())
    .optional(),
  datasourceId: z.number().optional(),
  datasource: z.lazy(() => DataSourceMetadataSchema.optional()).optional(),
  projectName: z.string().optional(),
  envName: z.string().optional(),
  envType: z.string().optional(),
  createdBy: z.string().optional(),
});

const ScriptCreateSchema = ZodUtils.makeOptionalPropsNullable(
  TaskScriptSchema.extend({
    name: z.string().min(1).regex(noWhitespaceAtTheBeginning, {
      message: "Script name should not start with whitespace",
    }),
  }),
).omit(GENERATED_BY_DB_PROPERTIES);

export const ScriptSchemas = {
  general: TaskScriptSchema,
  createData: ZodUtils.makeDefaultPropsOptional(
    ZodUtils.makeOptionalPropsNullable(
      TaskScriptSchema.extend({
        name: z.string().min(1).regex(noWhitespaceAtTheBeginning, {
          message: "Script name should not start with whitespace",
        }),
      })
        .omit(GENERATED_BY_DB_PROPERTIES)
        .merge(z.object({ tags: z.array(z.number()) })),
    ).partial(),
  ),
  create: ScriptCreateSchema,
  update: ZodUtils.makeOptionalPropsNullable(
    TaskScriptSchema.extend({
      name: z.string().min(1).regex(noWhitespaceAtTheBeginning, {
        message: "Script name should not start with whitespace",
      }),
    })
      .omit(GENERATED_BY_DB_PROPERTIES)
      .merge(z.object({ globalVariables: z.array(z.number()), tags: z.array(z.number()) })),
  ).partial(),
};

export const WorkItemDefinitionSchema = z.object({
  title: z.string(),
  priority: z.coerce.number(),
  type: z.enum(AllowedWorkItemTypes),
  stepLabel: z.string(),
  itsId: z.number(),
  id: z.string().optional(),
  severity: z.enum(WorkItemSeverity).optional(),
  reproSteps: z.string().optional(),
  stepLogId: z.number().optional().nullable(),
  iteration: z.string().optional(),
  area: z.string().nullable().optional(),
  screenshotPath: z.string().optional(),
  description: z.string().optional(),
});

export const TestCaseDefinitionSchema = WorkItemDefinitionSchema.omit({ stepLabel: true }).merge(
  z.object({
    testCaseSteps: z.string(),
  }),
);

export const BaseAzureIntegrationMetadata = z.object({
  testPlanId: z.union([z.string(), z.number(), z.null()]).transform((id) => (id || "").toString()), // todo: can be string or number or null
  testSuiteId: z.string(),
  itsId: z.number(),
  project: z.string(),
});

export const ProjectIntegrationMetadataSchema = z.object({
  azureDevops: z.any(),
  // BaseAzureIntegrationMetadata.extend({  todo: FIX MANY PROBLEMS
  //   testSuiteDefineUrl: z.string().optional(),
  //   testSuiteExecuteUrl: z.string().optional(),
  // }).optional(),
});

export const ProjectSchema = z.object({
  ...CommonProperties,

  path: z.string().default(PROJECT_DEFAULT_PATH),
  name: z.string().default("unknown name"), // todo: what to dooooo with types

  overrideGroups: z.boolean().default(false),
  overrideVirtualUsers: z.boolean().default(false),
  overrideSystems: z.boolean().default(false),
  overrideItsRules: z.boolean().default(false),

  description: z.string().default(""),

  category: z.number().optional(),
  dueDate: DateZodType.optional(),
  startDate: DateZodType.optional(),
  version: z.string().default(""),

  defaultSystemId: z.number().optional(),
  defaultVirtualUserId: z.number().optional(),

  runMode: z.string().default("SEQUENTIAL"),
  lastSchedulerId: z.number().optional(),
  statusesToRun: z.array(z.enum(ScriptStatus)).default([]),
  tagsToRun: z.array(z.number()).default([]),

  maximumParallelSessions: z.number().optional(),

  integrationMetadata: ProjectIntegrationMetadataSchema.optional(),
  executionParams: BackgroundStartParamsScheme.extend({
    mapResultWithDevops: z.boolean().default(false),
    azureConfigurationId: z.number().nullable().optional(),
  })
    .partial()
    .nullable()
    .optional(), // todo: figure our what options can be available
  documentationTemplate: z.number().optional(),
  reportTemplate: z.number().optional(),
  summaryTemplate: z.number().optional(),

  defaultVirtualUser: VirtualUserSchemas.general.optional(),
  defaultSystem: SystemSchema.optional(),

  // todo: attach normal types later
  owners: z.array(UserSchemaWithoutPass).default([]).optional(),
  groups: z.array(UserGroupSchema).default([]).optional(),
  virtualUsers: z.array(VirtualUserSchemas.general).default([]).optional(),
  systems: z.array(SystemSchema).default([]).optional(),
  itsRules: z.array(IssueTrackingRuleSchema).default([]).optional(),
});

const ProjectEdit = {
  name: z.string().min(1).optional(),
  maximumParallelSessions: z.coerce.number().min(1).optional(),
  owners: z.array(z.number()).default([]).optional(),
  groups: z.array(z.number()).default([]).optional(),
  virtualUsers: z.array(z.number()).default([]).optional(),
  systems: z.array(z.number()).default([]).optional(),
  itsRules: z.array(z.number()).default([]).optional(),
};

const ProjectCreateSchema = ZodUtils.makeOptionalPropsNullable(ProjectSchema.extend(ProjectEdit))
  .omit(GENERATED_BY_DB_PROPERTIES)
  .partial({
    path: true,
  });

export const ProjectSchemas = {
  editRelations: z.object(ProjectEdit),
  general: ProjectSchema,
  createData: ZodUtils.makeDefaultPropsOptional(ProjectCreateSchema),
  create: ProjectCreateSchema,
  update: ZodUtils.makeOptionalPropsNullable(ProjectSchema.extend(ProjectEdit)).partial(),
};

export const DateRangeSchema = z.object({
  from: DateZodType,
  to: DateZodType,
});

const SchedulerScriptSchema = z.object({
  ...CommonProperties,
  name: z.string(),
  lineNum: z.number(),
  description: z.string().default(""),
  scriptId: z.number(),
  runParams: z.any(),
  script: TaskScriptSchema.optional(),
});

export const SchedulerScriptSchemas = ZodUtils.generateEntitySchemas(SchedulerScriptSchema);

const SchedulerGroupSchema = z.object({
  ...CommonProperties,
  schedulerJobId: z.number(),
  lineNum: z.number(),
  description: z.string().default(""),
  runParams: z.any(),
  name: z.string().default(""),
  scripts: z.array(SchedulerScriptSchema).default([]),
  overrideRunParams: z.any(),
});

export const SchedulerGroupSchemas = ZodUtils.generateEntitySchemas(SchedulerGroupSchema);

export const SchedulerSchema = z.object({
  ...CommonProperties,
  name: z.string(),
  mode: z.enum(SchedulerMode).default(SchedulerMode.BASIC),
  runMode: z.enum(RepeaterTypes).default(RepeaterTypes.SEQUENTIAL),
  description: z.string().default(""),
  status: z.enum(JOB_STATUS_TYPE).default(JOB_STATUS_TYPE.INACTIVE),
  startDateTime: DateZodType.optional(),
  endDateTime: DateZodType.optional(),
  offset: z.coerce.number().min(1).optional(),
  occurrences: z.coerce.number().min(1).optional(),
  recurrenceType: z.enum(RecurrenceType).optional(),
  userId: z.number().optional(),
  triggerType: z.enum(JOB_TRIGGER_TYPE).optional(),
  storageId: z.number().optional(),
  storageTriggerContainer: z.string().optional(),
  notificationEmails: z.preprocess(
    (value: string | undefined) => (value ? value : ""),
    z.string().default(""),
  ),
  mapResultWithDevops: z.preprocess((value) => value ?? false, z.boolean().default(false)),
  isInternal: z.boolean().default(false),
  activatedBy: z.number().optional(),
  maximumParallelSessions: z.coerce.number().min(1).optional(),
  groups: z.array(SchedulerGroupSchema).default([]),
  integrationMetadata: z.preprocess(
    (value) => (value ? value : {}),
    z.object({
      azureDevops: z.object({ configurations: z.array(z.coerce.number()).optional() }).optional(),
    }),
  ),
  runParams: z
    .object({
      retryCount: z.coerce.number().optional(),
      virtualUserMode: z.enum(OverrideTypes).optional(),
      virtualUserId: z.preprocess((val) => (val === null ? undefined : val), z.number().optional()),
      virtualUserPoolId: z.preprocess(
        (val) => (val === null ? undefined : val),
        z.number().optional(),
      ),
      environmentId: z.preprocess((val) => (val === null ? undefined : val), z.number().optional()),
      takeScreenshots: z.boolean().optional(),
      screenshotsOnlyOnInterruption: z.boolean().optional(),
    })
    .optional(), // TODO: type forrunParams
  // resolvers: z.looseObject({}).catchall(z.any()), // TODO: type for resolvers
});

const SchedulerCreateSchema = ZodUtils.makeOptionalPropsNullable(
  SchedulerSchema.extend({
    name: z.string().min(1, { message: "Name cannot be empty" }).regex(noWhitespaceAtTheBeginning, {
      message: "Scheduler name should not start with whitespace",
    }),
  }),
).omit(GENERATED_BY_DB_PROPERTIES);

export const SchedulerSchemas = {
  general: SchedulerSchema,
  createData: ZodUtils.makeDefaultPropsOptional(SchedulerCreateSchema),
  create: SchedulerCreateSchema,
  update: ZodUtils.makeOptionalPropsNullable(SchedulerCreateSchema).partial(),
};

const ReportTemplateSchema = z.object({
  ...CommonProperties,
  name: z.string(),
  filename: z.string(),
  originalFilename: z.string(),
  description: z.string().default(""),
});

const ReportTemplateCreateSchema = ZodUtils.makeOptionalPropsNullable(
  ReportTemplateSchema.extend({
    name: z.string().regex(noWhitespaceAtTheBeginning, {
      message: "The template name must start with a character.",
    }),
  }),
).omit(GENERATED_BY_DB_PROPERTIES);

export const ReportTemplateSchemas = {
  general: ReportTemplateSchema,
  createData: ZodUtils.makeDefaultPropsOptional(ReportTemplateCreateSchema),
  create: ReportTemplateCreateSchema,
  update: ReportTemplateCreateSchema.partial(),
};

export const DefinedIssueTrackingSchema = z.object({
  itsId: z.coerce.number(),
  project: z.string().optional(),
});

export const NewIssueTrackingSchema = z.object({
  token: z.string(),
  host: z.string(),
  project: z.string().optional(),
});

export const AzureDevopsConnectionDataSchema = z.union([
  DefinedIssueTrackingSchema,
  NewIssueTrackingSchema,
]);

export const ScriptChangeLogSchema = z.object({
  ...CommonProperties,
  scriptId: z.number(),
  scriptName: z.string(),
  oldValue: z.enum(ScriptStatus).optional(),
  newValue: z.enum(ScriptStatus),
  user: z.string(),
  comment: z.string().default("").optional(),
  tags: z.array(z.string()).optional(),
  version: z.string().optional(),
  snapshot: z.any(),
  projectId: z.number().optional(),
  projectName: z.string().optional(),
});

export const ScriptChangeLogSchemas = ZodUtils.generateEntitySchemas(ScriptChangeLogSchema);

export const VirtualUserPoolSchema = z.object({
  ...CommonProperties,
  name: z.string(),
  description: z.string().default(""),
  virtualUsers: z.array(VirtualUserSchemas["general"]).optional(),
});

const VirtualUserPoolEdit = VirtualUserPoolSchema.extend({
  virtualUsers: z.array(z.number()),
});

const VirtualUserPoolCreateSchema = ZodUtils.makeOptionalPropsNullable(
  VirtualUserPoolEdit.extend({
    name: z.string().regex(noWhitespaceAtTheBeginning, {
      message: "Virtual Users Pool name should not start with whitespace",
    }),
  }),
).omit(GENERATED_BY_DB_PROPERTIES);

export const VirtualUserPoolSchemas = {
  general: VirtualUserPoolSchema,
  createData: ZodUtils.makeDefaultPropsOptional(VirtualUserPoolCreateSchema),
  create: VirtualUserPoolCreateSchema,
  update: VirtualUserPoolCreateSchema.partial(),
};

const WebsocketDataCommon = {
  wsId: z.string().optional(),
};

export const RemovingScreenshotsStatsSchema = z.object({
  isProcessing: z.boolean(),
  days: z.number(),
  stats: z.object({
    total: z.number(),
    done: z.number(),
  }),
});

export const SubscribeUpdateQuerySchema = z.object({
  taskScriptId: z.coerce.number().optional(),
  parentId: z.coerce.number().optional(),
});

export const WebsocketDataSchemas = {
  [WebsocketMessageTypes.OPEN_CONNECTION]: z.object({
    userId: z.coerce.number(),
    wsId: z.string(),
  }),

  [WebsocketMessageTypes.NOTIFICATION]: z.object({
    status: z.string().optional(),
    scriptName: z.string().optional(),
    message: z.string().optional(),
    ...WebsocketDataCommon,
  }),

  [WebsocketMessageTypes.REMOVE_CLIENT_COOKIES]: z.object({
    sessionId: z.string(),
    ...WebsocketDataCommon,
  }),

  [WebsocketMessageTypes.SCHEDULER]: z.object({
    ...WebsocketDataCommon,
    projectIds: z.array(z.number()),
    schedulerJobId: z.coerce.number(),
    schedulerJobName: z.string(),
    status: z.enum(EXECUTION_STATUS),
    jobId: z.string(),
    type: z.enum(["END", "START"]),
  }),

  [WebsocketMessageTypes.SUBSCRIBE_TO_UPDATES]: z.object({
    queryId: z.string(),
    wsId: z.string(),
    modelName: z.enum(SubscribeUpdateModelNames),
    query: SubscribeUpdateQuerySchema,
  }),

  [WebsocketMessageTypes.UNSUBSCRIBE_TO_UPDATES]: z.object({
    queryId: z.string(),
    ...WebsocketDataCommon,
  }),

  [WebsocketMessageTypes.PERFORM_REFRESH]: z.object({
    ...WebsocketDataCommon,
    queryId: z.string(),
  }),

  [WebsocketMessageTypes.HEARTBEAT]: z.object({ ...WebsocketDataCommon }),
  [WebsocketMessageTypes.EXPORT_GENERATION_START]: z.object({
    ...WebsocketDataCommon,
    jobId: z.string(),
  }),
  [WebsocketMessageTypes.EXPORT_GENERATION_PROGRESS]: z.object({
    ...WebsocketDataCommon,
    jobId: z.string(),
    progress: z.number(),
  }),
  [WebsocketMessageTypes.EXPORT_GENERATION_FAIL]: z.object({
    ...WebsocketDataCommon,
    jobId: z.string(),
    reason: z.string(),
  }),
  [WebsocketMessageTypes.EXPORT_GENERATION_SUCCESS]: z.object({
    ...WebsocketDataCommon,
    jobId: z.string(),
    fileName: z.string(),
    customFileName: z.string().optional(),
  }),
  [WebsocketMessageTypes.REPORT_GENERATION_START]: z.object({
    ...WebsocketDataCommon,
    reportName: z.string(),
  }),
  [WebsocketMessageTypes.REPORT_GENERATION_SUCCESS]: z.object({
    ...WebsocketDataCommon,
    reportName: z.string(),
    originalReportName: z.string(),
  }),

  [WebsocketMessageTypes.GLOBAL_ACTIONS_SCREENSHOTS_CLEANUP_UPDATE]: z.object({
    ...WebsocketDataCommon,
    stats: RemovingScreenshotsStatsSchema.extend({
      hasBeenCompleted: z.boolean().optional(),
      error: z.string().optional(),
    }),
  }),
  [WebsocketMessageTypes.REPORT_GENERATION_FAIL]: z.object({
    ...WebsocketDataCommon,
    reportName: z.string(),
    reason: z.string(),
  }),
};

const DocumentationSchema = z.object({
  ...CommonProperties,
  name: z.string(),
  prettyName: z.string(),
  scriptId: z.number(),
  sessionId: z.string().optional(),
  description: z.string(),
  path: z.string(),
  version: z.string(),
  locale: z.string(),
  signedBy: z.string().optional(),
  type: z.enum(DOCUMENTATION_TYPE),
  status: z.enum(DocumentationStatus),
  script: TaskScriptSchema.optional(),
});

export const DocumentationSchemas = ZodUtils.generateEntitySchemas(DocumentationSchema);

export const TestPlansQuerySchema = z.object({
  itsId: z.coerce.number(),
  project: z.string().optional(),
});
export const TestCasesQuerySchema = z
  .object({
    testPlanId: z.coerce.number(),
    testSuiteId: z.coerce.number(),
  })
  .merge(TestPlansQuerySchema);

export const TestSuiteSiblingsQuerySchema = z
  .object({
    testPlanId: z.coerce.number(),
    currentTestSuiteId: z.coerce.number(),
  })
  .merge(TestPlansQuerySchema);

export type UpdateProjectDevopsOptions = {
  shouldTriggerDevopsMappingUpdate: boolean;
  updateTestPlanOrSuite: boolean;
  remapTestPlan: boolean;
  remapTestSuite: boolean;
  isRootProject: boolean;
};

export const TaskScriptExportSchema = z.object({
  modelName: z.literal(ExportToCsvModels.SCRIPTS),
  wsId: z.string(),
  meta: z.object({
    customFilename: z.string().optional(),
    additionalFields: z.object({
      takeScreenshots: z.boolean().optional(),
      screenshotsOnlyOnInterruption: z.boolean().optional(),
      clickOnDisabledButtons: z.boolean().optional(),
      useDefaultEnvironment: z.boolean().optional(),
      topProjectName: z.boolean().optional(),
      virtualUserName: z.boolean().optional(),
    }),
  }),
  filter: z.any(),
});

export const ExecutionLogExportSchema = z.object({
  modelName: z.literal(ExportToCsvModels.LOGS),
  wsId: z.string(),
  meta: z.object({
    customFilename: z.string().optional(),
    additionalFields: z.object({
      scriptLogUrl: z.boolean().default(false),
      schedulerJobName: z.boolean().default(false),
      jobId: z.boolean().default(false),
      lastErrorMessage: z.boolean().default(false),
    }),
  }),
  filter: z.any(),
});

export const ExecutionStepLogExportSchema = z.object({
  modelName: z.literal(ExportToCsvModels.STEP_LOGS),
  wsId: z.string(),
  meta: z.object({
    customFilename: z.string().optional(),
  }),
  filter: z.any(),
});

export const DashboardDataSchemas = {
  diskUsage: z.object({
    sizeGB: z.number(),
    usedGB: z.number(),
    availableGB: z.number(),
  }),
  plannedScheduler: z.object({
    executionDate: z.coerce.number(),
    id: z.coerce.number(),
    schedulerJobName: z.string(),
  }),
};

const columnFilterSchema = z.object({
  id: z.string(),
  value: z.union([
    z.array(z.string().nullable().optional()),
    z.array(z.number().nullable().optional()),
    z.array(z.date().nullable().optional()),
    z.string(),
    z.number(),
    z.boolean(),
  ]),
});

const commonColumnFilterElements = {
  page: z.coerce.number().optional().default(1),
  sorting: z.array(z.object({ id: z.string(), desc: booleanSchema })).optional(),
};

export const SetTableQueryParamsSchema = z.object({
  ...commonColumnFilterElements,
  columnFilters: z
    .array(
      z.preprocess((arrayItem: { id: "string"; value: number | string | string[] | Date[] }) => {
        if (Array.isArray(arrayItem?.value)) {
          return {
            id: arrayItem.id,
            value: arrayItem.value.map((value) => {
              return value?.toISOString ? value.toISOString() : value;
            }),
          };
        }
        return arrayItem;
      }, columnFilterSchema),
    )
    .optional(),
});

export const GetTableQueryParamsSchema = z.object({
  ...commonColumnFilterElements,
  columnFilters: z
    .array(
      z.preprocess(
        (arrayItem: { id: "string"; value: number | string | string[] | Date[] }) => {
          const { id, value } = arrayItem;
          if (Array.isArray(value)) {
            return {
              id,
              value: value.map(convertValue),
            };
          }
          if (value) return { id, value: convertValue(value) };
          return arrayItem;
        },
        z.object({ id: z.string(), value: z.any() }),
      ),
    )
    .optional(),
});

const ResolverSegmentSchema = z.object({
  meta: z
    .object({
      tagName: z.string().optional(),
      id: z.string().optional(),
    })
    .optional(),
  path: z.string(),
  type: z.enum(["CUSTOM", "BASIC"]),
});

const PositionResolverSchema = z.object({
  type: z.literal(ResolverType.POSITION),
  position: z.object({
    top: z.number(),
    left: z.number(),
  }),
  resolution: z.object({
    width: z.number(),
    height: z.number(),
  }),
});

const PathResolverSchema = z.object({
  path: z.string(),
  type: z.literal(ResolverType.XPATH),
});

const SelectorResolverSchema = z.object({
  path: z.string(),
  type: z.literal(ResolverType.SELECTOR),
});

const SmartXpathSegmentsResolverSchema = z.object({
  type: z.literal(ResolverType.SMART_XPATH_SEGMENTS),
  segments: z.array(ResolverSegmentSchema),
});

export const ResolverSchema = z.discriminatedUnion("type", [
  PathResolverSchema,
  SelectorResolverSchema,
  SmartXpathSegmentsResolverSchema,
  PositionResolverSchema,
]);

export const ResolversArraySchema = z.array(ResolverSchema);

export const ResolversSchema = z.object({
  metadata: z.array(z.record(z.string(), z.unknown())),
  data: ResolversArraySchema,
});

export const AuditTrailSchema = z.object({
  id: z.number(),
  tableName: z.string(),
  relid: z.number(),
  username: z.string().optional(),
  tstampTx: DateZodType,
  tstampStm: DateZodType,
  tstampClk: DateZodType,
  transactionId: z.number().optional(),
  clientaddr: z.string().optional(),
  clientPort: z.number().optional(),
  clientQuery: z.string().optional(),
  action: z.string(),
  rowData: z.any().optional(),
  changedFields: z.any().optional(),
  statementOnly: z.boolean().optional(),
  rowId: z.number().optional(),
  oldData: z.any().optional(),
  newData: z.any().optional(),
  metadata: z
    .object({
      scriptId: z.number().optional().nullable(),
      projectId: z.number().optional().nullable(),
      isSystemSettings: z.boolean().optional().nullable(),
    })
    .optional()
    .nullable(),
});

const AuditTrailViewsCommonSchema = AuditTrailSchema.pick({
  id: true,
  tableName: true,
  username: true,
  action: true,
  newData: true,
  oldData: true,
  changedFields: true,
  tstampStm: true,
});

export const AuditTrailSettingsViewSchema = AuditTrailViewsCommonSchema.extend({
  createdAt: DateZodType,
  isSystemSettings: z.boolean(),
});

export const AuditTrailScriptViewSchema = AuditTrailViewsCommonSchema.extend({
  createdAt: DateZodType,
  scriptId: z.number(),
});

export const AuditTrailProjectViewSchema = AuditTrailViewsCommonSchema.extend({
  createdAt: DateZodType,
  projectId: z.number(),
});
